import { forwardRef } from 'react';

interface ToggleProps {
  checked: boolean;
  onChange: (checked: boolean) => void;
  disabled?: boolean;
  label?: string;
  description?: string;
  size?: 'sm' | 'md' | 'lg';
  className?: string;
}

const Toggle = forwardRef<HTMLInputElement, ToggleProps>(
  ({ 
    checked, 
    onChange, 
    disabled = false, 
    label, 
    description, 
    size = 'md', 
    className = '' 
  }, ref) => {
    
    const sizeClasses = {
      sm: {
        switch: 'h-4 w-7',
        thumb: 'h-3 w-3',
        translate: checked ? 'translate-x-3' : 'translate-x-0.5'
      },
      md: {
        switch: 'h-5 w-9',
        thumb: 'h-4 w-4',
        translate: checked ? 'translate-x-4' : 'translate-x-0.5'
      },
      lg: {
        switch: 'h-6 w-11',
        thumb: 'h-5 w-5',
        translate: checked ? 'translate-x-5' : 'translate-x-0.5'
      }
    };

    const sizes = sizeClasses[size];
    
    const handleChange = () => {
      if (!disabled) {
        onChange(!checked);
      }
    };

    const switchClasses = `
      ${sizes.switch}
      relative inline-flex items-center rounded-full 
      oro-transition cursor-pointer
      ${checked 
        ? 'bg-accent shadow-glow' 
        : 'bg-surface-alt border border-border hover:border-border-hover'
      }
      ${disabled ? 'opacity-50 cursor-not-allowed' : ''}
      focus-within:oro-focus
    `.trim();

    const thumbClasses = `
      ${sizes.thumb} ${sizes.translate}
      inline-block rounded-full oro-transition
      ${checked 
        ? 'bg-black shadow-lg' 
        : 'bg-text-secondary shadow-md'
      }
    `.trim();

    return (
      <div className={`flex items-start gap-3 ${className}`}>
        <label className="relative flex items-center cursor-pointer">
          <input
            ref={ref}
            type="checkbox"
            checked={checked}
            onChange={handleChange}
            disabled={disabled}
            className="sr-only"
          />
          <span className={switchClasses}>
            <span className={thumbClasses} />
          </span>
        </label>
        
        {(label || description) && (
          <div className="flex-1 min-w-0">
            {label && (
              <p className="text-sm font-medium text-text-primary">
                {label}
              </p>
            )}
            {description && (
              <p className="text-xs text-text-muted mt-0.5">
                {description}
              </p>
            )}
          </div>
        )}
      </div>
    );
  }
);

Toggle.displayName = 'Toggle';

export default Toggle;